import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Autocomplete,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from '@mui/material';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  AreaChart,
  Area,
  ResponsiveContainer,
} from 'recharts';
import { ActivityCalendar } from 'react-activity-calendar';
import {
  engagementApi,
  type EngagementSummary,
  type DailyActivity,
  type HourlyPattern,
  type AppBreakdown,
  type DomainBreakdown,
  type ActiveUser,
  type KnownUser,
} from '../services/engagementApi';

function formatDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function daysAgo(n: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

const KpiCard: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <Paper sx={{ p: 2, flex: 1, minWidth: 140, textAlign: 'center' }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h5" fontWeight={700}>{value}</Typography>
  </Paper>
);

const UserEngagementPage: React.FC = () => {
  const { accessToken } = useSelector((state: RootState) => (state as any).auth);
  const [knownUsers, setKnownUsers] = useState<KnownUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<KnownUser[]>([]);
  const [dateRange, setDateRange] = useState({
    from: formatDate(daysAgo(30)),
    to: formatDate(new Date()),
  });

  const [summary, setSummary] = useState<EngagementSummary | null>(null);
  const [daily, setDaily] = useState<DailyActivity[]>([]);
  const [hourly, setHourly] = useState<HourlyPattern[]>([]);
  const [byApp, setByApp] = useState<AppBreakdown[]>([]);
  const [byDomain, setByDomain] = useState<DomainBreakdown[]>([]);
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [loading, setLoading] = useState(false);

  // Load known users once
  useEffect(() => {
    engagementApi.getKnownUsers(accessToken).then(setKnownUsers).catch(() => {});
  }, [accessToken]);

  const userIds = useMemo(
    () => selectedUsers.map((u) => u.userId),
    [selectedUsers],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = { from: dateRange.from, to: dateRange.to, userIds };
    try {
      const [s, d, h, a, dom, users] = await Promise.all([
        engagementApi.getSummary(params, accessToken),
        engagementApi.getDailyActivity(params, accessToken),
        engagementApi.getHourlyPattern(params, accessToken),
        engagementApi.getByApp(params, accessToken),
        engagementApi.getByDomain(params, accessToken),
        engagementApi.getActiveUsers({ from: dateRange.from, to: dateRange.to, limit: 50 }, accessToken),
      ]);
      setSummary(s);
      setDaily(d);
      setHourly(h);
      setByApp(a);
      setByDomain(dom);
      setActiveUsers(users);
    } catch {
      // silently fail — TODO: show snackbar
    } finally {
      setLoading(false);
    }
  }, [dateRange, userIds, accessToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Prepare calendar data for react-activity-calendar
  const calendarData = useMemo(() => {
    if (!daily.length) return [];
    const maxCount = Math.max(...daily.map((d) => d.count), 1);
    return daily.map((d) => ({
      date: d.date,
      count: d.count,
      level: Math.min(4, Math.ceil((d.count / maxCount) * 4)) as 0 | 1 | 2 | 3 | 4,
    }));
  }, [daily]);

  // Spider chart data (domains)
  const spiderData = useMemo(() => {
    return byDomain.map((d) => ({
      domain: d.domain,
      count: d.count,
      fullMark: Math.max(...byDomain.map((x) => x.count), 1),
    }));
  }, [byDomain]);

  // Top app entry for KPI
  const topApp = useMemo(() => {
    if (!summary?.byApp) return '-';
    const entries = Object.entries(summary.byApp);
    if (!entries.length) return '-';
    entries.sort((a, b) => (b[1] as number) - (a[1] as number));
    return entries[0][0];
  }, [summary]);

  const busiestHourLabel = useMemo(() => {
    if (summary?.busiestHour == null) return '-';
    return `${String(summary.busiestHour).padStart(2, '0')}:00`;
  }, [summary]);

  return (
    <Box sx={{ p: 0 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        User Engagement
      </Typography>

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center" flexWrap="wrap">
        <Autocomplete
          multiple
          options={knownUsers}
          getOptionLabel={(u) => u.userEmail || u.userId}
          value={selectedUsers}
          onChange={(_, val) => setSelectedUsers(val)}
          renderTags={(value, getTagProps) =>
            value.map((u, index) => (
              <Chip
                {...getTagProps({ index })}
                key={u.userId}
                label={u.userEmail || u.userId.slice(0, 8)}
                size="small"
              />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label="Filter users" size="small" sx={{ minWidth: 300 }} />
          )}
          sx={{ minWidth: 300 }}
        />
        <TextField
          label="From"
          type="date"
          size="small"
          value={dateRange.from}
          onChange={(e) => setDateRange((prev) => ({ ...prev, from: e.target.value }))}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={dateRange.to}
          onChange={(e) => setDateRange((prev) => ({ ...prev, to: e.target.value }))}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        {loading && <CircularProgress size={20} />}
      </Stack>

      {/* KPI Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
        <KpiCard label="Total Actions" value={summary?.totalActions?.toLocaleString() ?? '-'} />
        <KpiCard label="Unique Users" value={summary?.uniqueUsers ?? '-'} />
        <KpiCard label="Most Used App" value={topApp} />
        <KpiCard label="Busiest Hour" value={busiestHourLabel} />
      </Stack>

      {/* Calendar Heatmap */}
      {calendarData.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Activity Calendar</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <ActivityCalendar
              data={calendarData}
              blockSize={12}
              blockMargin={3}
              fontSize={12}
              theme={{
                dark: ['#161b22', '#0e4429', '#006d32', '#26a641', '#39d353'],
                light: ['#ebedf0', '#9be9a8', '#40c463', '#30a14e', '#216e39'],
              }}
            />
          </Box>
        </Paper>
      )}

      {/* Charts row */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        {/* Spider Chart — Domains */}
        {spiderData.length > 0 && (
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Domains</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={spiderData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="domain" />
                <PolarRadiusAxis />
                <Radar name="Activity" dataKey="count" stroke="#1976d2" fill="#1976d2" fillOpacity={0.3} />
              </RadarChart>
            </ResponsiveContainer>
          </Paper>
        )}

        {/* App Bar Chart */}
        {byApp.length > 0 && (
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="subtitle2" gutterBottom>By App</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={byApp} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="app" width={100} />
                <Tooltip />
                <Bar dataKey="count" fill="#1976d2" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        )}
      </Stack>

      {/* Hourly Pattern */}
      {hourly.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Activity by Hour of Day</Typography>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={hourly}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" tickFormatter={(h: number) => `${String(h).padStart(2, '0')}:00`} />
              <YAxis />
              <Tooltip labelFormatter={(h: number) => `${String(h).padStart(2, '0')}:00`} />
              <defs>
                <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1976d2" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#1976d2" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="count" stroke="#1976d2" fill="url(#colorCount)" />
            </AreaChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* User Leaderboard */}
      {activeUsers.length > 0 && !selectedUsers.length && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Top Active Users</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeUsers.map((u, i) => (
                  <TableRow key={u.userId}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{u.userEmail || u.userId.slice(0, 8)}</TableCell>
                    <TableCell align="right">{u.count.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default UserEngagementPage;
